// src/reduxStore/slices/cartSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { mainApi } from "../../api/axios";

/**
 * Search customers
 */
export const searchCustomer = createAsyncThunk(
  "cart/searchCustomer",
  async ({ mobileNo }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/customers/search?mobile_no=${mobileNo}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Add customer
 */
export const addCustomer = createAsyncThunk(
  "cart/addCustomer",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.post("/v1/pos/customers", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Set customer
 */
export const setCustomerDetails = createAsyncThunk(
  "cart/setCustomerDetails",
  async (obj, { rejectWithValue }) => {
    try {
      return obj;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/**
 * Load table (order + kots) by table id or order uid.
 * Backend should return the same shape you posted earlier.
 */
export const loadTable = createAsyncThunk(
  "cart/loadTable",
  async ({ dine_area_table_uid, order_uid } = {}, { rejectWithValue }) => {
    try {
      // Either pass table id or order uid
      const res = await mainApi.get(
        `/v1/pos/tables/${dine_area_table_uid || ""}?order_uid=${
          order_uid || ""
        }`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Create KOT (send pending items to kitchen)
 * payload: { order_uid, items: [...], comment }
 */
export const createKot = createAsyncThunk(
  "cart/createKot",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.post(`/v1/pos/orders`, obj);

      const order = res.data;
      clearDraftOrderInSession();
      setActiveOrderUidInSession(order?.order_uid);

      return order;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/**
 * Update KOT (send pending items to kitchen)
 */
export const updateKot = createAsyncThunk(
  "cart/updateKot",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await mainApi.put(`/v1/pos/orders`, obj);

      const order = res.data;
      clearDraftOrderInSession();
      // setActiveOrderUidInSession(order?.table?.order?.order_uid);
      return order;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 *
 * Get active order by uid
 */
export const getActiveOrderByUid = createAsyncThunk(
  "cart/getActiveOrderByUid",
  async ({ storeId, orderUid }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/orders/details?store_id=${storeId}&order_id=${orderUid}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Store the active order in localStorage
 */
export const setActiveOrderUidInSession = (orderUid) => {
  localStorage.setItem("active_order_uid", orderUid);
};

/**
 * Clear the active order from localStorage
 */
export const clearActiveOrderUidInSession = () => {
  localStorage.removeItem("active_order_uid");
};

/**
 * Clear draft items from localStorage
 */
export const clearDraftOrderInSession = () => {
  localStorage.removeItem("draft_order");
};
/**
 * Optional: settle order
 */
export const settleOrder = createAsyncThunk(
  "cart/settleOrder",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const res = await mainApi.patch("/v1/pos/orders/settle", payload);
      clearActiveOrderUidInSession();
      if (!payload?.settleFromCounter) {
        dispatch(clearCart());
      }
      res.data.dine_area_table_uid = payload.dine_area_table_uid;
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get all tables with status ex. Free, Occupied, Reserved
 */
export const getTablesWithStatus = createAsyncThunk(
  "cart/getTablesWithStatus",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `v1/pos/dine/table/status?store_id=${storeId}&limit=1000&offset=0`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update order table
 */
export const updateOrderTable = createAsyncThunk(
  "cart/updateOrderTable",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await mainApi.patch("v1/pos/orders/change-table", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update order type
 */
export const updateOrderType = createAsyncThunk(
  "cart/updateOrderType",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await mainApi.patch("v1/pos/orders/type", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Cancel order
 */
export const cancelOrder = createAsyncThunk(
  "cart/cancelOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await mainApi.patch("v1/pos/orders/status", payload);
      clearActiveOrderUidInSession();
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete Item from order
 */
export const deleteOrderItem = createAsyncThunk(
  "cart/deleteOrderItem",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await mainApi.patch("v1/pos/orders/item", payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get all kots for an order
 */
export const getOrderKots = createAsyncThunk(
  "cart/getOrderKots",
  async ({ storeId, activeOrderId }, { rejectWithValue }) => {
    try {
      const res = await mainApi.get(
        `/v1/pos/orders/kot-details?store_id=${storeId}&order_id=${activeOrderId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Normalize addons for signature comparison
const normalizeAddons = (addons = []) =>
  addons
    .map((a) => ({
      id: a.product_addon_uid,
      qty: a.quantity,
    }))
    .sort((a, b) => a.id - b.id);

// Build unique signature for an item based on variant, attributes, addons
const buildSignature = (item) =>
  JSON.stringify({
    variant_uid: item.variant_uid,
    attributes: item.attributes || {},
    addons: normalizeAddons(item.selectedAddons || []),
  });

// Generate unique cart item ID
const generateCartItemId = () =>
  crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

const initialState = {
  activeOrderId: null,
  orderId: null,
  lastAddedItemId: {},
  orderInfo: {},
  existingKots: [],
  pendingItems: [],
  selectedTable: {},
  note: "",
  discount: { type: null, value: 0, reason: "" },
  orderType: {},
  customerSearchResults: {},
  customerDetails: {},
  tablesWithStatus: [],
  loading: false,
  error: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState: initialState,
  reducers: {
    // select table when user clicks a table in UI
    selectTable: (state, action) => {
      const tblDetails = action.payload;
      const dine = tblDetails.dine || {};
      state.selectedTable = dine;
      state.activeOrderId = tblDetails?.order_uid || null;
      state.orderId = tblDetails?.order_id || null;
      state.orderInfo = tblDetails || {};
      // backend order kots -> existingKots
      state.existingKots = tblDetails?.kots || [];
      // pendingItems start empty when loading
      state.pendingItems = [];

      if (tblDetails?.order_uid) {
        state.customerDetails = tblDetails?.customer || {};
        state.orderType = tblDetails?.order_type_id || {};
      } else {
        state.orderType = { type: 1 }; // default to Dine-in
      }
    },
    //Update table change for order transfer
    updateTableChange: (state, action) => {
      const tblDetails = action.payload;
      console.log("updateTableChange", tblDetails);
      state.selectedTable = {
        dine_area_name: tblDetails.dine_area_name,
        dine_area_uid: tblDetails.dine_area_uid,
        table: {
          chairs: tblDetails.chairs,
          dine_area_table_id: tblDetails.dine_area_table_id,
          dine_area_table_uid: tblDetails.dine_area_table_uid,
          order_dine_table_id: tblDetails.order_dine_table_id,
          order_dine_table_status_id: tblDetails.order_dine_table_status_id,
          order_dine_table_uid: tblDetails.order_dine_table_uid,
          table_name: tblDetails.name,
        },
      };
    },
    // Pending item CRUD — only affect pendingItems
    addItem: (state, action) => {
      const payload = action.payload;
      const incomingSignature = buildSignature(payload);

      const existing = state.pendingItems.find(
        (i) => i.signature === incomingSignature
      );
      if (existing) {
        existing.quantity = (existing.quantity || 0) + (payload.quantity ?? 1);
        existing.note = payload.note ?? existing.note;
      } else {
        state.pendingItems.push({
          ...payload,
          quantity: payload.quantity ?? 1,
          note: payload.note ?? "",
          signature: incomingSignature,
          cartItemId: generateCartItemId(),
        });
      }
    },

    updateItem: (state, action) => {
      const { variant_uid, quantity, note } = action.payload;
      const item = state.pendingItems.find(
        (i) => i.variant_uid === variant_uid
      );
      if (item) {
        if (quantity !== undefined) item.quantity = quantity;
        if (note !== undefined) item.note = note;
      }
    },

    removeItem: (state, action) => {
      state.pendingItems = state.pendingItems.filter(
        (i) => i.variant_uid !== action.payload.variant_uid
      );
    },

    clearPendingItems: (state) => {
      state.pendingItems = [];
      state.discount = { type: null, value: 0, reason: "" };
    },

    addItemNote: (state, action) => {
      const { variant_uid, note } = action.payload;
      const item = state.pendingItems.find(
        (i) => i.variant_uid === variant_uid
      );
      if (item) {
        if (note !== undefined) item.note = note;
      }
    },

    removeItemNote: (state, action) => {
      const { variant_uid, note } = action.payload;
      const item = state.pendingItems.find(
        (i) => i.variant_uid === variant_uid
      );
      if (item) {
        if (note !== undefined) item.note = note;
      }
    },

    restoreDraftOrder: (state, action) => {
      const draft = action.payload;

      console.log("Restoring draft order", draft);

      state.selectedTable = draft.selectedTable || {};
      state.orderType = draft.orderType || {};
      state.customerDetails = draft.customerDetails || {};
      state.discount = draft.discount || { type: null, value: 0, reason: "" };
      state.orderInfo = draft.orderInfo || {};
      state.pendingItems = draft.pendingItems || [];
    },

    addNote: (state, action) => {
      state.note = action.payload;
    },

    removeNote: (state, action) => {
      state.note = "";
    },

    removeOrderCustomer: (state) => {
      state.customerSearchResults = {};
      state.customerDetails = {};
    },
    setOrderType: (state, action) => {
      state.orderType = action.payload;
    },

    setDiscount: (state, action) => {
      state.discount = action.payload;
    },

    setLastAddedItemId: (state, action) => {
      state.lastAddedItemId = action.payload;
    },

    clearDiscount: (state) => {
      state.discount = { type: null, value: 0, reason: "" };
    },

    clearSelectedTable: (state) => {
      state.selectedTable = {};
    },

    resetCartKeepOrderType: (state) => {
      Object.assign(state, {
        ...initialState,
        orderType: state.orderType,
      });
    },

    clearCart: () => initialState,
  },
  extraReducers: (builder) => {
    // loadTable
    builder
      .addCase(loadTable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTable.fulfilled, (state, action) => {
        state.loading = false;
        const table = action.payload?.data?.table;
        if (table) {
          state.selectedTable = {
            dine_area_table_uid: table.dine_area_table_uid,
            table_name: table.table_name,
            chairs: table.chairs,
            sequence: table.sequence,
          };

          const order = table.order || {};
          state.activeOrderId = order.order_uid || null;
          state.orderId = order.order_id || null;
          state.existingKots = order.kots || [];
          state.note = "";
          state.customerDetails = order.customer || {};
        }
      })
      .addCase(loadTable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });

    // createKot (send pending items)
    builder
      .addCase(createKot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createKot.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        console.log("createKot.fulfilled", action.payload);

        // Backend returns the latest order with kots under data.table.order.kots
        const newKotList = payload?.kots || [];

        // Replace existingKots with authoritative backend list
        state.existingKots = newKotList;

        // Clear pending items because they are sent to kitchen
        state.pendingItems = [];

        // Clear note after sending KOT
        state.note = "";

        state.selectedTable = payload.dine || state.selectedTable;
        state.activeOrderId = payload?.order_uid || state.activeOrderId;
        state.orderId = payload?.order_id || state.order_id;
        state.orderInfo = payload || state.orderInfo;
        state.customerDetails = payload?.customer || state.customerDetails;

        if (
          payload?.order_type_id !== undefined &&
          payload?.order_type_id !== null
        ) {
          state.orderType = { type: payload.order_type_id };
        }
      })
      .addCase(createKot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });

    // updateKot (send pending items)
    builder
      .addCase(updateKot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateKot.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // Backend returns the latest order with kots under data.table.order.kots
        const newKotList = payload?.kots || [];
        // Replace existingKots with authoritative backend list
        state.existingKots = newKotList;
        // Clear pending items because they are sent to kitchen
        state.pendingItems = [];
        // Clear note after sending KOT
        state.note = "";
        // update activeOrderId/note/customer if backend returned
        state.activeOrderId = payload?.order_uid || state.activeOrderId;
        state.orderId = payload?.order_id || state.order_id;
        state.customerDetails = payload?.customer || state.customerDetails;
        if (
          payload?.order_type_id !== undefined &&
          payload?.order_type_id !== null
        ) {
          state.orderType = { type: payload.order_type_id };
        }
      })
      .addCase(updateKot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });

    // Get active order
    builder
      .addCase(getActiveOrderByUid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveOrderByUid.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // Backend returns the latest order with kots under data.table.order.kots
        const newKotList = payload?.kots || [];
        // Replace existingKots with authoritative backend list
        state.existingKots = newKotList;
        // Clear note after sending KOT
        state.note = "";
        state.activeOrderId = payload?.order_uid || state.activeOrderId;
        state.orderId = payload?.order_id || state.order_id;
        state.orderInfo = payload || state.orderInfo;
        state.customerDetails = payload?.customer || state.customerDetails;
        if (
          payload?.order_type_id !== undefined &&
          payload?.order_type_id !== null
        ) {
          state.orderType = { type: payload.order_type_id };
        }
      })
      .addCase(getActiveOrderByUid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });

    // settleOrder (optional)
    builder
      .addCase(settleOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(settleOrder.fulfilled, (state, action) => {
        state.loading = false;
        // backend may return updated order (paid/closed). If so, clear cart.
        // Example: clear cart when order settled
        state.activeOrderId = null;
        state.existingKots = [];
        state.pendingItems = [];
        state.selectedTable = {};
      })
      .addCase(settleOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });

    builder
      // ADD Customer
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customerSearchResults = {};
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
      })
      // SEARCH Customer
      .addCase(searchCustomer.pending, (state, action) => {
        state.customerSearchResults = {};
      })
      .addCase(searchCustomer.fulfilled, (state, action) => {
        state.customerSearchResults = action.payload;
      })
      // Set Customer Details
      .addCase(setCustomerDetails.fulfilled, (state, action) => {
        state.customerDetails = action.payload;
        state.customerSearchResults = {};
      });
    //Set Tables with Status
    builder.addCase(getTablesWithStatus.fulfilled, (state, action) => {
      const tableList = action.payload || [];
      state.tablesWithStatus = tableList;
    });
    // Get order kots
    builder
      .addCase(getOrderKots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderKots.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getOrderKots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error;
      });
  },
});

export const {
  addItem,
  updateItem,
  removeItem,
  clearPendingItems,
  addItemNote,
  removeItemNote,
  addNote,
  removeNote,
  removeOrderCustomer,
  setDiscount,
  clearDiscount,
  selectTable,
  updateTableChange,
  resetCartKeepOrderType,
  clearCart,
  setOrderType,
  setLastAddedItemId,
  clearSelectedTable,
  restoreDraftOrder,
} = cartSlice.actions;

export default cartSlice.reducer;
